import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BookingConfirmed } from "./components/BookingConfirmed";
import { PageWidth } from "./components/travel-ui";
import { PointsCashPay } from "./components/PointsCashPay";
import { useMarketplaceSearch } from "./context/SearchContext";
import { getFlightById } from "./data/mock-flights";
import { formatMoney, formatLongDate } from "./lib/format";
import { clampPoints, quoteFlight } from "./lib/pricing";
import { useMemberAuth } from "./auth/MemberAuthContext";
import { saveTrip } from "./services/trip.service";
import { useWallet } from "./services/wallet.service";

export default function MemberFlightBook() {
  const { flightId = "" } = useParams();
  const { search } = useMarketplaceSearch();
  const { session } = useMemberAuth();
  const wallet = useWallet();
  const flight = getFlightById(flightId);
  const cabinFares =
    flight?.fares.filter((fare) => fare.cabin === search.cabin) ?? [];
  const fares = cabinFares.length ? cabinFares : flight?.fares ?? [];
  const [fareId, setFareId] = useState(fares[0]?.id ?? "");
  const fare = fares.find((item) => item.id === fareId) ?? fares[0];
  const quote = useMemo(
    () => (fare ? quoteFlight(fare, search.passengers) : null),
    [fare, search.passengers],
  );

  const [name, setName] = useState(session?.fullName || "");
  const [email, setEmail] = useState(session?.channel === "email" ? session.contact : "");
  const [mobile, setMobile] = useState(session?.channel === "mobile" ? session.contact : "");
  useEffect(() => {
    if (!wallet.loaded) return;
    setName((value) => value || wallet.fullName);
    setEmail((value) => value || wallet.email);
    setMobile((value) => value || wallet.mobile);
  }, [wallet.loaded, wallet.fullName, wallet.email, wallet.mobile]);
  const [points, setPoints] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<null | {
    reference: string;
    tierBefore: string;
    tierAfter: string;
    cashPaid: number;
    pointsUsed: number;
    pointsEarned: number;
    earnPending: boolean;
    earnAvailableAt: string | null;
  }>(null);

  if (!flight || !fare || !quote) {
    return (
      <PageWidth>
        <p className="font-semibold">That flight is not in the demo list.</p>
        <Link to="/member/flights" className="text-[#006ce4]">
          Back to flights
        </Link>
      </PageWidth>
    );
  }

  if (done) {
    return (
      <PageWidth>
        <BookingConfirmed
          reference={done.reference}
          title={`${flight.fromCity} to ${flight.toCity}`}
          lines={[
            `${flight.airline} ${flight.number} · ${fare.name}`,
            `${formatLongDate(search.departDate)} · ${flight.depart}–${flight.arrive}`,
            `${search.passengers} passenger${search.passengers === 1 ? "" : "s"}`,
            `Traveller: ${name}`,
          ]}
          cashPaid={done.cashPaid}
          currency={wallet.currency}
          pointsUsed={done.pointsUsed}
          pointsEarned={done.pointsEarned}
          earnPending={done.earnPending}
          earnAvailableAt={done.earnAvailableAt}
          tierBefore={done.tierBefore}
          tierAfter={done.tierAfter}
        />
      </PageWidth>
    );
  }

  const used = clampPoints(points, quote.total, wallet.points, wallet.pointValue);
  const cash = Math.max(0, Math.round((quote.total - used * wallet.pointValue) * 100) / 100);

  const confirm = () => {
    if (name.trim().length < 2) {
      setError("Enter the traveller name.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Enter an email for the ticket.");
      return;
    }
    if (mobile.replace(/\D/g, "").length < 10) {
      setError("Enter a mobile number.");
      return;
    }
    setError("");
    setSubmitting(true);
    void saveTrip({
      kind: "flight",
      title: `${flight.fromCity} to ${flight.toCity}`,
      subtitle: `${flight.airline} · ${flight.number} · ${fare.name}`,
      image: "",
      startDate: search.departDate,
      endDate: search.tripType === "round" ? search.returnDate : search.departDate,
      detail: `${flight.depart} ${flight.fromCode} – ${flight.arrive} ${flight.toCode} · ${flight.stops === 0 ? "Direct" : `${flight.stops} stop`}`,
      travellers: search.passengers,
      totalInr: quote.total,
      pointsUsed: used,
      guest: { name: name.trim(), email: email.trim(), mobile: mobile.trim() },
    })
      .then((result) => {
        setDone({
          reference: result.trip.reference,
          tierBefore: result.tierBefore,
          tierAfter: result.tierAfter,
          cashPaid: result.trip.cashPaid,
          pointsUsed: result.trip.pointsUsed,
          pointsEarned: result.trip.pointsEarned,
          earnPending: result.earnState === "pending",
          earnAvailableAt: result.earnAvailableAt,
        });
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Could not confirm.");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <PageWidth>
      <Link to="/member/flights" className="text-sm font-semibold text-[#006ce4]">
        Back to flights
      </Link>
      <h1 className="mt-2 text-2xl font-bold">
        {flight.fromCity} to {flight.toCity}
      </h1>
      <p className="text-sm text-[#595959]">
        {flight.airline} {flight.number} · {formatLongDate(search.departDate)} ·{" "}
        {flight.depart}–{flight.arrive} ·{" "}
        {flight.stops === 0 ? "Direct" : `${flight.stops} stop`}
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
            <h2 className="text-lg font-bold">1. Choose a fare</h2>
            <div className="mt-4 grid gap-3">
              {fares.map((option) => {
                const optionQuote = quoteFlight(option, search.passengers);
                const selected = option.id === fare.id;
                return (
                  <label
                    key={option.id}
                    className={`flex cursor-pointer items-start justify-between gap-3 rounded-md border p-3 ${
                      selected ? "border-[#006ce4] bg-[#f3f8ff]" : "border-[#e7e7e7]"
                    }`}
                  >
                    <span>
                      <input
                        type="radio"
                        name="fare"
                        className="mr-2"
                        checked={selected}
                        onChange={() => setFareId(option.id)}
                      />
                      <span className="font-semibold">
                        {option.name} · {option.cabin}
                      </span>
                      <span className="mt-1 block text-sm text-[#595959]">
                        {option.baggage}
                        {option.refundable ? " · Refundable" : " · Non-refundable"}
                        {option.seatsLeft ? ` · ${option.seatsLeft} seats left` : ""}
                      </span>
                    </span>
                    <span className="text-right font-bold">
                      {formatMoney(optionQuote.total, wallet.currency)}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="rounded-lg border border-[#e7e7e7] bg-white p-5">
            <h2 className="text-lg font-bold">2. Lead traveller</h2>
            <p className="mt-1 text-sm text-[#595959]">
              Other passengers on this booking use the same ticket reference.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-medium sm:col-span-2">
                Full name
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3"
                />
              </label>
              <label className="text-sm font-medium">
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3"
                />
              </label>
              <label className="text-sm font-medium">
                Mobile
                <input
                  value={mobile}
                  onChange={(event) => setMobile(event.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-[#e7e7e7] px-3"
                />
              </label>
            </div>
          </section>

          <PointsCashPay
            total={quote.total}
            availablePoints={wallet.points}
            points={used}
            pointValue={wallet.pointValue}
            currency={wallet.currency}
            onPointsChange={setPoints}
          />
          {error ? (
            <p className="text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            disabled={submitting}
            onClick={confirm}
            className="h-12 w-full rounded-md bg-[#006ce4] text-base font-semibold text-white hover:bg-[#0057b8] disabled:opacity-60"
          >
            {submitting
              ? "Confirming…"
              : `Pay ${new Intl.NumberFormat("en-IN", { style: "currency", currency: wallet.currency || "AED", maximumFractionDigits: wallet.pointValue < 1 ? 2 : 0 }).format(cash)}${used ? ` + ${used.toLocaleString("en-IN")} pts` : ""}`}
          </button>
          <p className="text-xs text-[#595959]">
            This confirms the flight on your membership. No card is charged in this checkout.
          </p>
        </div>

        <aside className="h-fit rounded-lg border border-[#e7e7e7] bg-white p-4 text-sm lg:sticky lg:top-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#595959]">
            Your flight
          </p>
          <h2 className="mt-1 text-lg font-bold">
            {flight.fromCode} → {flight.toCode}
          </h2>
          <p>
            {flight.depart} – {flight.arrive}
          </p>
          <p className="text-[#595959]">
            {flight.duration} · {flight.stops === 0 ? "Direct" : `${flight.stops} stop`}
          </p>
          <p className="mt-2">{fare.name}</p>
          <dl className="mt-4 space-y-1 border-t border-[#e7e7e7] pt-3">
            <div className="flex justify-between">
              <dt>Fare</dt>
              <dd>{formatMoney(quote.base, wallet.currency)}</dd>
            </div>
            <div className="flex justify-between text-[#008009]">
              <dt>Member fare</dt>
              <dd>−{formatMoney(quote.discount, wallet.currency)}</dd>
            </div>
            <div className="flex justify-between font-bold">
              <dt>Total</dt>
              <dd>{formatMoney(quote.total, wallet.currency)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </PageWidth>
  );
}
